import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Select, MenuItem,
  Switch, IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, Stack, Alert, CircularProgress, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import DeleteIcon from '@mui/icons-material/Delete'
import LogoutIcon from '@mui/icons-material/Logout'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import TuneIcon from '@mui/icons-material/Tune'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import {
  useAdminUsers, useSetUserRole, useRemoveUser, useForceLogoutUser, useToggleUserNotifications,
  useSetAccountEnabled, useSetUserLimits,
  useSetTestAlertsEnabled,
  useAdminEvents, useDeleteAdminEvents,
  useAllowedEmails, useAddAllowedEmail, useRemoveAllowedEmail, useUpdateAllowedEmailNote,
} from '../hooks/useAdmin'
import { useAccount } from '../hooks/useAccount'
import type { AdminUser, AdminEventType, AdminEventSeverity, OAuthProvider } from '../types'

// ── Tab panel helper ──────────────────────────────────────────────────────────
function TabPanel({ value, index, children }: { value: number; index: number; children: React.ReactNode }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null
}

// ── Confirmation dialog ────────────────────────────────────────────────────────

function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', onConfirm, onCancel,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dialogBorderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
      slotProps={{ paper: { sx: { borderRadius: 3, border: `1px solid ${dialogBorderColor}` } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} variant="outlined" color="inherit">{t('common.cancel')}</Button>
        <Button onClick={onConfirm} variant="contained" color="error" autoFocus>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Provider chip ─────────────────────────────────────────────────────────────
function ProviderChip({ provider }: { provider?: OAuthProvider }) {
  const { t } = useTranslation()
  return (
    <Chip
      label={provider === 'MICROSOFT' ? t('common.providerMicrosoft') : t('common.providerGoogle')}
      size="small"
      sx={{
        background: provider === 'MICROSOFT' ? 'rgba(0,114,240,0.15)' : 'rgba(66,133,244,0.15)',
        color: provider === 'MICROSOFT' ? '#0072F0' : '#4285F4',
        fontWeight: 600,
      }}
    />
  )
}

// ── Severity chip color mapping ───────────────────────────────────────────────
function severityColor(severity: AdminEventSeverity): 'error' | 'warning' | 'info' {
  if (severity === 'ERROR') return 'error'
  if (severity === 'WARN') return 'warning'
  return 'info'
}

// ── Limits dialog ─────────────────────────────────────────────────────────────
function LimitsDialog({
  user,
  onClose,
}: {
  user: AdminUser | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [phones, setPhones] = useState(String(user?.maxPhoneNumbers ?? ''))
  const [channels, setChannels] = useState(String(user?.maxNotificationChannels ?? ''))
  const { mutate: setLimits, isPending } = useSetUserLimits()

  if (!user) return null

  const handleSave = () => {
    setLimits(
      {
        userId: user.userId,
        maxPhoneNumbers: parseInt(phones) || 2,
        maxNotificationChannels: parseInt(channels) || 5,
      },
      { onSuccess: onClose }
    )
  }

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.users.limitsTitle', { name: user.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label={t('admin.users.maxPhones')}
            type="number"
            value={phones}
            onChange={(e) => setPhones(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { min: 1, max: 10 } }}
          />
          <TextField
            label={t('admin.users.maxChannels')}
            type="number"
            value={channels}
            onChange={(e) => setChannels(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { min: 1, max: 20 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={isPending}>
          {isPending ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab() {
  const { t } = useTranslation()
  const { data: users, isLoading } = useAdminUsers()
  const { mutate: setRole } = useSetUserRole()
  const { mutate: removeUser } = useRemoveUser()
  const { mutate: forceLogout } = useForceLogoutUser()
  const { mutate: toggleNotifications } = useToggleUserNotifications()
  const { mutate: setAccountEnabled } = useSetAccountEnabled()
  const { mutate: setTestAlertsEnabled } = useSetTestAlertsEnabled()
  const [limitsUser, setLimitsUser] = useState<AdminUser | null>(null)
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null)
  const [logoutUser, setLogoutUser] = useState<AdminUser | null>(null)
  const [confirmUser, setConfirmUser] = useState<AdminUser | null>(null)

  if (isLoading) return <CircularProgress />

  return (
    <>
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.users.nameEmail')}</TableCell>
              <TableCell>{t('admin.users.lastSeen')}</TableCell>
              <TableCell>{t('admin.users.provider')}</TableCell>
              <TableCell>{t('admin.users.role')}</TableCell>
              <TableCell align="center">{t('admin.users.notifs')}</TableCell>
              <TableCell align="center">{t('admin.users.accountCol')}</TableCell>
              <TableCell align="center">{t('admin.users.testAlerts')}</TableCell>
              <TableCell align="center">{t('admin.users.limits')}</TableCell>
              <TableCell align="center">{t('admin.users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(users ?? []).map((u) => (
              <TableRow key={u.userId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={u.lastSeenAt ? 'text.primary' : 'text.disabled'} noWrap>
                    {u.lastSeenAt ? dayjs(u.lastSeenAt).fromNow() : t('admin.users.never')}
                  </Typography>
                </TableCell>
                <TableCell><ProviderChip provider={u.provider} /></TableCell>
                <TableCell>
                  <FormControl size="small" variant="outlined">
                    <Select
                      value={u.role}
                      onChange={(e) => setRole({ userId: u.userId, role: e.target.value })}
                      sx={{ minWidth: 90 }}
                    >
                      <MenuItem value="USER">{t('admin.users.user')}</MenuItem>
                      <MenuItem value="ADMIN">{t('admin.users.admin')}</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={u.notificationsEnabled}
                    onChange={(e) =>
                      toggleNotifications({ userId: u.userId, enabled: e.target.checked })
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={u.enabled}
                    color="success"
                    onChange={(e) =>
                      setAccountEnabled({ userId: u.userId, enabled: e.target.checked })
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t('admin.users.testAlertsTooltip')}>
                    <Switch
                      size="small"
                      checked={u.testAlertsEnabled}
                      color="warning"
                      onChange={(e) =>
                        setTestAlertsEnabled({ userId: u.userId, enabled: e.target.checked })
                      }
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t('admin.users.limitsTooltip', { phones: u.maxPhoneNumbers, channels: u.maxNotificationChannels })}>
                    <IconButton size="small" onClick={() => setLimitsUser(u)}>
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                    <Tooltip title={t('admin.users.forceLogout')}>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => { setConfirmUser(u); setLogoutUser(u) }}
                      >
                        <LogoutIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.users.deleteUser')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setConfirmUser(u); setDeleteUser(u) }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <LimitsDialog key={limitsUser?.userId} user={limitsUser} onClose={() => setLimitsUser(null)} />
      <ConfirmDialog
        open={!!logoutUser}
        title={t('admin.users.forceLogout')}
        message={t('admin.users.forceLogoutConfirm', { name: confirmUser?.name, email: confirmUser?.email })}
        confirmLabel={t('admin.users.forceLogoutBtn')}
        onConfirm={() => {
          if (logoutUser) forceLogout(logoutUser.userId)
          setLogoutUser(null)
        }}
        onCancel={() => setLogoutUser(null)}
      />
      <ConfirmDialog
        open={!!deleteUser}
        title={t('admin.users.deleteUser')}
        message={t('admin.users.deleteConfirm', { name: confirmUser?.name, email: confirmUser?.email })}
        confirmLabel={t('admin.users.deleteBtn')}
        onConfirm={() => {
          if (deleteUser) removeUser(deleteUser.userId)
          setDeleteUser(null)
        }}
        onCancel={() => setDeleteUser(null)}
      />
    </>
  )
}

// ── Event type label helpers ──────────────────────────────────────────────────
const ALL_EVENT_TYPES: AdminEventType[] = ['RUNTIME_EXCEPTION', 'INCIDENT', 'TEST_ALERT', 'SYSTEM', 'AUTH', 'ACCESS_DENIED']

// ── System Events tab ─────────────────────────────────────────────────────────
function SystemEventsTab() {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'

  const EVENT_TYPE_LABELS: Record<AdminEventType, string> = {
    RUNTIME_EXCEPTION: t('admin.events.types.RUNTIME_EXCEPTION'),
    INCIDENT: t('admin.events.types.INCIDENT'),
    TEST_ALERT: t('admin.events.types.TEST_ALERT'),
    SYSTEM: t('admin.events.types.SYSTEM'),
    AUTH: t('admin.events.types.AUTH'),
    ACCESS_DENIED: t('admin.events.types.ACCESS_DENIED'),
  }

  const [selectedType, setSelectedType] = useState<AdminEventType | ''>('RUNTIME_EXCEPTION')
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const [pageIdx, setPageIdx] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const PAGE_SIZE = 20

  const eventType = selectedType === '' ? null : selectedType
  // Send the picked calendar dates as-is; the backend resolves day boundaries in the requesting
  // admin's account timezone so "To = 23rd" includes the whole 23rd as that user sees it.
  const fromParam = fromDate ? fromDate.format('YYYY-MM-DD') : null
  const toParam = toDate ? toDate.format('YYYY-MM-DD') : null
  const currentCursor = cursorStack[pageIdx] ?? null
  const { data, isLoading } = useAdminEvents(eventType, fromParam, toParam, currentCursor, PAGE_SIZE)
  const { mutate: deleteEvents, isPending: isDeleting } = useDeleteAdminEvents()

  // Server-side date filtering (GSI range query) — no client-side trimming of the page.
  const events = data?.items ?? []

  const hasNext = !!data?.nextCursor
  const hasPrev = pageIdx > 0

  // Any filter change must restart pagination from the first page.
  const resetPaging = () => {
    setCursorStack([null])
    setPageIdx(0)
    setSelected(new Set())
  }

  const handleFromChange = (v: Dayjs | null) => {
    setFromDate(v)
    resetPaging()
  }

  const handleToChange = (v: Dayjs | null) => {
    setToDate(v)
    resetPaging()
  }

  const handleClearDates = () => {
    setFromDate(null)
    setToDate(null)
    resetPaging()
  }

  const allSelected = events.length > 0 && events.every((e) => selected.has(e.eventId))
  const someSelected = events.some((e) => selected.has(e.eventId))

  const handleTypeChange = (type: AdminEventType | '') => {
    setSelectedType(type)
    setCursorStack([null])
    setPageIdx(0)
    setSelected(new Set())
  }

  const handleNext = () => {
    if (!data?.nextCursor) return
    const nextIdx = pageIdx + 1
    setCursorStack((prev) => {
      const updated = [...prev]
      if (nextIdx >= updated.length) updated.push(data.nextCursor!)
      return updated
    })
    setPageIdx(nextIdx)
    setSelected(new Set())
  }

  const handlePrev = () => {
    if (pageIdx > 0) setPageIdx((p) => p - 1)
    setSelected(new Set())
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(events.map((e) => e.eventId)))
    }
  }

  const toggleSelect = (eventId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) next.delete(eventId)
      else next.add(eventId)
      return next
    })
  }

  const handleDeleteConfirm = () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    deleteEvents(ids, {
      onSuccess: () => {
        setSelected(new Set())
        setConfirmDeleteOpen(false)
      },
    })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        {/* Filters row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel shrink>{t('admin.events.type')}</InputLabel>
            <Select<AdminEventType | ''>
              value={selectedType}
              label={t('admin.events.type')}
              displayEmpty
              notched
              renderValue={(value) =>
                value === '' ? t('admin.events.allTypes') : EVENT_TYPE_LABELS[value as AdminEventType]
              }
              onChange={(e) => handleTypeChange(e.target.value as AdminEventType | '')}
            >
              <MenuItem value="">{t('admin.events.allTypes')}</MenuItem>
              {ALL_EVENT_TYPES.map((tp) => (
                <MenuItem key={tp} value={tp}>{EVENT_TYPE_LABELS[tp]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date range — always side-by-side so they read as a pair on mobile too */}
          <Stack direction="row" spacing={1} sx={{ flex: { sm: 'none' }, width: { xs: '100%', sm: 'auto' } }}>
            <DatePicker
              label={t('admin.events.from')}
              value={fromDate}
              maxDate={toDate ?? undefined}
              onChange={handleFromChange}
              slotProps={{ textField: { size: 'small', sx: { flex: 1, width: { sm: 150 } } } }}
            />
            <DatePicker
              label={t('admin.events.to')}
              value={toDate}
              minDate={fromDate ?? undefined}
              onChange={handleToChange}
              slotProps={{ textField: { size: 'small', sx: { flex: 1, width: { sm: 150 } } } }}
            />
          </Stack>
          {(fromDate || toDate) && (
            <Button size="small" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              onClick={handleClearDates}>
              {t('admin.events.clearDates')}
            </Button>
          )}
        </Stack>

        {/* Loading / empty states */}
        {isLoading && <CircularProgress size={28} />}

        {!isLoading && events.length === 0 && (
          <Alert severity="success" variant="outlined">
            {t('admin.events.noEvents')}
            {selectedType ? ` ${t('admin.events.forType', { type: EVENT_TYPE_LABELS[selectedType] })}` : ''}.
          </Alert>
        )}

        {/* Events list */}
        {!isLoading && events.length > 0 && (
          <Stack spacing={1} sx={{ overflow: 'hidden' }}>
            {/* Select all + delete row */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1, py: 0.5 }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleSelectAll}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {someSelected ? t('admin.events.selected', { count: selected.size }) : t('admin.events.selectAll')}
              </Typography>
              {someSelected && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('admin.events.deleting') : t('admin.events.deleteSelected')}
                </Button>
              )}
            </Stack>

            {events.map((e) => (
              <Accordion
                key={e.eventId}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    overflow: 'hidden',
                    '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden', alignItems: 'center' },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selected.has(e.eventId)}
                    onClick={(ev) => ev.stopPropagation()}
                    onChange={() => toggleSelect(e.eventId)}
                    sx={{ mr: 1, flexShrink: 0 }}
                  />
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 0.75, md: 2 }}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ flex: 1, pr: 2, minWidth: 0, overflow: 'hidden' }}
                  >
                    {/* Mobile-only timestamp on its own line so it is never truncated */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      {e.timestamp ? dayjs(e.timestamp).format('DD.MM.YYYY HH:mm') : ''}
                    </Typography>
                    {/* Chips — wrap instead of squeezing the row on narrow screens */}
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      alignItems="center"
                      sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}
                    >
                      <Chip
                        label={e.severity}
                        size="small"
                        color={severityColor(e.severity)}
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      />
                      <Chip
                        label={EVENT_TYPE_LABELS[e.eventType as AdminEventType] ?? e.eventType}
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0, opacity: 0.7 }}
                      />
                    </Stack>
                    {/* Message: wraps up to 2 lines on mobile, single-line ellipsis on desktop */}
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                        whiteSpace: { xs: 'normal', md: 'nowrap' },
                        display: { xs: '-webkit-box', md: 'block' },
                        WebkitLineClamp: { xs: 2, md: 'unset' },
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {e.message ?? '—'}
                    </Typography>
                    {/* Timestamp on desktop only (mobile shows it next to the chips above) */}
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }} noWrap>
                      {e.timestamp ? dayjs(e.timestamp).format('DD.MM.YYYY HH:mm') : ''}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {e.message && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {e.message}
                    </Typography>
                  )}
                  {e.userId && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      {t('common.user')}: {e.userId}
                    </Typography>
                  )}
                  {e.stackTrace && (
                    <Box
                      component="pre"
                      sx={{
                        fontSize: '0.72rem',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        // Readable high-contrast text; severity is conveyed by the tinted background
                        // and a colored left border rather than coloring the whole trace.
                        color: 'text.primary',
                        m: 0,
                        p: 1.5,
                        borderLeft: '3px solid',
                        borderColor: e.severity === 'ERROR'
                          ? 'error.main'
                          : e.severity === 'WARN'
                            ? 'warning.main'
                            : 'info.main',
                        background: e.severity === 'ERROR'
                          ? 'rgba(211,47,47,0.08)'
                          : e.severity === 'WARN'
                            ? 'rgba(237,108,2,0.08)'
                            : 'rgba(2,136,209,0.08)',
                        borderRadius: 1,
                        maxHeight: 360,
                        overflowY: 'auto',
                      }}
                    >
                      {e.stackTrace}
                    </Box>
                  )}
                  {!e.message && !e.stackTrace && (
                    <Typography variant="caption" color="text.secondary">
                      {t('common.noDetails')}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}

        {/* Prev / Next navigation */}
        {!isLoading && (hasPrev || hasNext) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrev}
              disabled={!hasPrev}
            >
              {t('admin.events.previous')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={handleNext}
              disabled={!hasNext}
            >
              {t('admin.events.next')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              {!hasNext
                ? t('admin.events.pageLast', { n: pageIdx + 1 })
                : t('admin.events.page', { n: pageIdx + 1 })}
            </Typography>
          </Stack>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('admin.events.deleteTitle')}
        message={t('admin.events.deleteConfirm', { count: selected.size })}
        confirmLabel={t('admin.users.deleteBtn')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </LocalizationProvider>
  )
}

// ── Email allowlist helpers ────────────────────────────────────────────────────

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const WILDCARD_RE = /^\*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

type EntryKind = 'EMAIL' | 'WILDCARD' | 'REGEX' | 'INVALID'

function classifyEntry(value: string): EntryKind {
  if (EMAIL_RE.test(value)) return 'EMAIL'
  if (WILDCARD_RE.test(value)) return 'WILDCARD'
  try { new RegExp(value); return 'REGEX' } catch { return 'INVALID' }
}

// ── Email allowlist tab ────────────────────────────────────────────────────────
function AllowlistTab() {
  const { t } = useTranslation()
  const { data: account } = useAccount()

  const { data: emails, isLoading } = useAllowedEmails()
  const { mutate: add, isPending: adding } = useAddAllowedEmail()
  const { mutate: remove } = useRemoveAllowedEmail()
  const { mutate: updateNote, isPending: savingNote } = useUpdateAllowedEmailNote()

  const [newEmail, setNewEmail] = useState('')
  const [newNote, setNewNote] = useState('')
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [editEmail, setEditEmail] = useState<string | null>(null)
  const [editNote, setEditNote] = useState('')

  const startEdit = (email: string, note?: string) => { setEditEmail(email); setEditNote(note ?? '') }
  const cancelEdit = () => { setEditEmail(null); setEditNote('') }
  const saveEdit = () => {
    if (!editEmail) return
    updateNote({ email: editEmail, note: editNote.trim() }, { onSuccess: cancelEdit })
  }

  const isInvalid = newEmail.trim() ? classifyEntry(newEmail.trim()) === 'INVALID' : false

  const handleAdd = () => {
    if (!newEmail.trim() || isInvalid) return
    add({ email: newEmail.trim(), note: newNote.trim() || undefined }, {
      onSuccess: () => { setNewEmail(''); setNewNote('') }
    })
  }

  if (isLoading) return <CircularProgress />

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>{t('admin.allowlist.addTitle')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            size="small"
            label={t('admin.allowlist.emailLabel')}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t('admin.allowlist.emailPlaceholder')}
            error={isInvalid}
            helperText={isInvalid ? t('admin.allowlist.validationError') : undefined}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { maxLength: 254 } }}
          />
          <TextField
            size="small"
            label={t('admin.allowlist.noteLabel')}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!newEmail.trim() || isInvalid || adding}
            sx={{ height: 40, mt: 0 }}
          >
            {t('admin.allowlist.add')}
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.allowlist.tableEmail')}</TableCell>
              <TableCell>{t('admin.allowlist.tableAddedBy')}</TableCell>
              <TableCell>{t('admin.allowlist.tableAddedAt')}</TableCell>
              <TableCell>{t('admin.allowlist.tableNote')}</TableCell>
              <TableCell align="center">{t('admin.allowlist.tableRemove')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(emails ?? []).map((e) => {
              return (
                <TableRow key={e.email} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{e.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={e.addedBy === account?.userId ? 'text.secondary' : 'text.primary'}>
                      {e.addedBy === account?.userId
                        ? t('common.me')
                        : e.addedBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {e.addedAt ? dayjs(e.addedAt).format('DD.MM.YYYY') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {editEmail === e.email ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          size="small"
                          autoFocus
                          value={editNote}
                          onChange={(ev) => setEditNote(ev.target.value)}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') saveEdit(); if (ev.key === 'Escape') cancelEdit() }}
                          placeholder={t('admin.allowlist.noteLabel')}
                          slotProps={{ htmlInput: { maxLength: 255 } }}
                          sx={{ minWidth: 160 }}
                        />
                        <Tooltip title={t('admin.allowlist.saveNote')}>
                          <span>
                            <IconButton size="small" color="primary" onClick={saveEdit} disabled={savingNote}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('common.cancel')}>
                          <IconButton size="small" onClick={cancelEdit} disabled={savingNote}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {e.note ?? '—'}
                          </Typography>
                          {e.lastEditedAt && (
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                              {t('admin.allowlist.lastEdited', { time: dayjs(e.lastEditedAt).format('DD.MM.YYYY HH:mm') })}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title={t('admin.allowlist.editNote')}>
                          <IconButton size="small" onClick={() => startEdit(e.email, e.note)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => { setConfirmEmail(e.email); setConfirmRemove(e.email) }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!confirmRemove}
        title={t('admin.allowlist.tableRemove')}
        message={t('admin.allowlist.removeConfirm', { email: confirmEmail })}
        confirmLabel={t('admin.users.deleteBtn')}
        onConfirm={() => {
          if (confirmRemove) remove(confirmRemove)
          setConfirmRemove(null)
        }}
        onCancel={() => setConfirmRemove(null)}
      />
    </Stack>
  )
}

// ── Main Admin page ────────────────────────────────────────────────────────────
export default function AdminPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t('admin.tabs.users')} />
        <Tab label={t('admin.tabs.events')} />
        <Tab label={t('admin.tabs.allowlist')} />
      </Tabs>

      <TabPanel value={tab} index={0}><UsersTab /></TabPanel>
      <TabPanel value={tab} index={1}><SystemEventsTab /></TabPanel>
      <TabPanel value={tab} index={2}><AllowlistTab /></TabPanel>
    </Box>
  )
}
